import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, ChevronRight, Search } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import useSWR from 'swr'

import { Money, SegmentedTabs, StatStrip, StatusBadge } from '@/components/ds'
import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { apiFetcher } from '@/data/fetcher'
import { formatDateTime } from '@/lib/formats/formatDateTime'

import {
  BankAccount,
  BankAccountTransaction,
  docTypeLabel,
  formatTxDate,
  matchTransaction,
  txAmount,
  txSubtitle,
  txTitle,
  useMatchIndex,
} from './bankUtils'
import { TransactionSheet } from './TransactionSheet'

type Filter = 'all' | 'in' | 'out' | 'unmatched'

const LOAD_SIZE = 50

const payments = (n: number) => `${n} ${n === 1 ? 'platba' : n >= 2 && n <= 4 ? 'platby' : 'plateb'}`

/** Souhrn, filtry a tabulka transakcí jednoho účtu, detail v pravém panelu (Mercury + Xero). */
export const BankTransactions = ({ account }: { account: BankAccount }) => {
  const [offset, setOffset] = useState(0)
  const [previousPages, setPreviousPages] = useState<BankAccountTransaction[]>([])
  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<BankAccountTransaction | null>(null)

  const { data, isLoading } = useSWR<{ data?: BankAccountTransaction[]; total?: number }>(
    `/api/v2/bank/${account.id}/transactions?limit=${LOAD_SIZE}&offset=${offset}`,
    apiFetcher,
  )

  const pageTransactions = useMemo(() => data?.data ?? [], [data])
  const all = useMemo(() => [...previousPages, ...pageTransactions], [previousPages, pageTransactions])
  const total = data?.total
  const canLoadMore = pageTransactions.length === LOAD_SIZE

  const loadMore = useCallback(() => {
    if (!isLoading && canLoadMore) {
      setPreviousPages((prev) => [...prev, ...pageTransactions])
      setOffset((prev) => prev + LOAD_SIZE)
    }
  }, [isLoading, canLoadMore, pageTransactions])

  const years = useMemo(() => {
    const set = new Set<number>()
    for (const t of all) if (t.date) set.add(new Date(t.date).getFullYear())
    return Array.from(set).sort()
  }, [all])
  const { index } = useMatchIndex(years)

  const rows = useMemo(() => all.map((t) => ({ t, match: matchTransaction(t, index) })), [all, index])

  const summary = useMemo(() => {
    let income = 0
    let expense = 0
    let inCount = 0
    let outCount = 0
    let unmatched = 0
    for (const { t, match } of rows) {
      const a = txAmount(t)
      if (a > 0) {
        income += a
        inCount += 1
      } else {
        expense += a
        outCount += 1
      }
      if (!match) unmatched += 1
    }
    const dates = rows.map((r) => r.t.date).filter(Boolean) as string[]
    const sorted = [...dates].sort()
    return { income, expense, net: income + expense, inCount, outCount, unmatched, from: sorted[0], to: sorted[sorted.length - 1] }
  }, [rows])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return rows.filter(({ t, match }) => {
      const a = txAmount(t)
      if (filter === 'in' && a <= 0) return false
      if (filter === 'out' && a > 0) return false
      if (filter === 'unmatched' && match) return false
      if (!q) return true
      return [t.payer, t.message, t.comment, t.note, t.vs, match?.doc.symbol].some((v) => v?.toLowerCase().includes(q))
    })
  }, [rows, filter, query])

  const currency = account.currency || account.balance.currency
  const selectedMatch = selected ? matchTransaction(selected, index) : null
  const firstLoad = isLoading && offset === 0

  const periodHint = summary.from ? `${formatTxDate(summary.from)} až ${formatTxDate(summary.to)}` : 'Zatím bez transakcí'

  const empty = !firstLoad && rows.length === 0

  return (
    <>
      {!empty && (
        <StatStrip
          className="mb-6"
          items={[
            {
              label: 'Příjmy',
              value: <Money amount={summary.income} currency={currency} size="lg" />,
              hint: payments(summary.inCount),
              active: filter === 'in',
              onClick: () => setFilter(filter === 'in' ? 'all' : 'in'),
            },
            {
              label: 'Výdaje',
              value: <Money amount={summary.expense} currency={currency} size="lg" />,
              hint: payments(summary.outCount),
              active: filter === 'out',
              onClick: () => setFilter(filter === 'out' ? 'all' : 'out'),
            },
            {
              label: 'Čistá změna',
              value: <Money amount={summary.net} currency={currency} size="lg" signed tone />,
              hint: periodHint,
            },
          ]}
        />
      )}

      {!empty && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <SegmentedTabs<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'Vše', count: rows.length },
              { value: 'in', label: 'Příjmy', count: summary.inCount },
              { value: 'out', label: 'Výdaje', count: summary.outCount },
              { value: 'unmatched', label: 'Nespárované', count: summary.unmatched },
            ]}
          />
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Protistrana, zpráva nebo VS" className="pl-8" />
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="hidden w-32 md:table-cell">Datum</TableHead>
              <TableHead>Protistrana a zpráva</TableHead>
              <TableHead className="hidden w-32 md:table-cell">VS</TableHead>
              <TableHead className="hidden w-48 md:table-cell">Párování</TableHead>
              <TableHead className="text-right md:w-40">Částka</TableHead>
              <TableHead className="hidden w-10 md:table-cell" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {firstLoad &&
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <TableCell key={j} className={j === 1 || j === 4 ? undefined : 'hidden md:table-cell'}>
                      <Skeleton className="h-4 w-full max-w-32" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!firstLoad &&
              filtered.map(({ t, match }) => {
                const a = txAmount(t)
                const sub = txSubtitle(t)
                return (
                  <TableRow key={t.id} onClick={() => setSelected(t)} className="group cursor-pointer">
                    <TableCell className="hidden tabular md:table-cell">{formatTxDate(t.date)}</TableCell>
                    <TableCell className="w-full max-w-0 md:w-auto">
                      <div className="flex items-center gap-2.5">
                        <span className="grid size-6 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground" aria-hidden>
                          {a > 0 ? <ArrowDownLeft className="size-3.5" /> : <ArrowUpRight className="size-3.5" />}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate">{txTitle(t)}</p>
                          {sub && <p className="hidden truncate text-xs text-muted-foreground md:block">{sub}</p>}
                          <p className="truncate text-xs text-muted-foreground tabular md:hidden">
                            {[formatTxDate(t.date), sub].filter(Boolean).join(', ')}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden tabular text-muted-foreground md:table-cell">{t.vs || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <StatusBadge status={match ? 'matched' : 'unmatched'} label={match ? 'Spárováno' : 'Nespárováno'} />
                      {match && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground tabular">
                          {docTypeLabel[match.type]} {match.doc.symbol}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Money amount={a} currency={t.amount?.currency ?? currency} signed tone />
                      <div className="mt-0.5 md:hidden">
                        <StatusBadge status={match ? 'matched' : 'unmatched'} label={match ? 'Spárováno' : 'Nespárováno'} />
                      </div>
                    </TableCell>
                    <TableCell className="hidden text-right md:table-cell">
                      <ChevronRight className="ml-auto size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                    </TableCell>
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>

        {empty && (
          <div className="p-6">
            <EmptyState icon={ArrowLeftRight}
              title="Zatím žádné transakce"
              description={
                account.fetchEnabled === 'enabled'
                  ? `Pohyby na účtu stahujeme automaticky.${account.lastFetch ? ` Naposledy ${formatDateTime(account.lastFetch)}.` : ' První transakce se objeví po prvním stažení.'}`
                  : 'Stahování transakcí je vypnuté, proto tu zatím nic není.'
              }
            />
          </div>
        )}

        {!firstLoad && rows.length > 0 && filtered.length === 0 && (
          <div className="p-6">
            <EmptyState icon={ArrowLeftRight} title="Žádné transakce neodpovídají filtru" description="Zkuste jiný filtr nebo hledaný výraz." />
          </div>
        )}

        {rows.length > 0 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
            <span className="tabular">
              {rows.length}
              {total ? ` z ${total}` : ''} transakcí
            </span>
            {canLoadMore && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={isLoading}>
                {isLoading ? 'Načítám' : 'Načíst další'}
              </Button>
            )}
          </div>
        )}
      </div>

      <TransactionSheet transaction={selected} match={selectedMatch} account={account} onOpenChange={(o) => !o && setSelected(null)} />
    </>
  )
}
